import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Check,
  ChevronRight,
  CircleAlert,
  FilePenLine,
  Funnel,
  Loader,
  Search,
  SquareArrowOutUpRight,
  Trash2,
  X,
} from 'lucide-react'

import { triggerToast, UserDisplay } from '@/components'
import { EmptySearchResult } from '@/components/EmptySearchResult'
import { Table } from '@/components/Table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { isAllowUpload } from '@/features/deliverable-set'
import {
  ApprovalInformationBox,
  InviteUsersDialog,
} from '@/features/deliverable-set/components'
import AddDeliverableSetDialog from '@/features/deliverable-set/components/DeliverableSetDialog'
import { useDeliverableSetById } from '@/features/deliverable-set/hooks'
import { useDeliverableSetsStore } from '@/features/deliverable-set/store'
import {
  DocumentListAction,
  getDocumentListColumns,
  useDocumentList,
  useDocumentStore,
} from '@/features/document'
import DeleteDocumentDialog from '@/features/document/components/DeleteDocumentDialog'
import { useTflViewerStore } from '@/features/view-tfl-uploaded/store'
import {
  ApiError,
  DeliverableSetSyncStatus,
  EDocumentFilterOnly,
  ResponseDocumentListItem,
} from '@/schema'
import { SortOrder } from '@/types/common'
import { cn, formatDisplayDate } from '@/utils'
import {
  DATE_FORMAT,
  DEFAULT_PAGE,
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_DELAY_MS,
} from '@/utils/constants'
import {
  buildDeliverableSetDocument,
  buildFileUploadFromDetailRoute,
  ROUTES,
} from '@/utils/routes'

import { useAuthStore } from '../../features/auth/index.ts'
import CancelApprovalRequestDialog from '../../features/deliverable-set/components/CancelApprovalRequestDialog.tsx'
import DelegateDialog from '../../features/deliverable-set/components/DelegateDialog.tsx'
import DeliverableSetHistoryDrawer from '../../features/deliverable-set/components/DeliverableSetHistoryDrawer.tsx'
import SendToReviewDialog from '../../features/deliverable-set/components/SendToReviewDialog.tsx'
import { ERoleType } from '../../features/user-management/index.ts'
import { isUserHasPermissionDeliverableSet } from '../../utils/roleBasedRouting.ts'

const DeliverableSetDetailPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const { data: deliverableSet, isLoading, error } = useDeliverableSetById(id)
  const { setEditingDeliverableSet } = useDeliverableSetsStore()
  const { setOpenDocument, setActiveDocumentId } = useDocumentStore()
  const [historyOpen, setHistoryOpen] = useState<boolean>(false)
  const getListCompanyUserHasAccess = useAuthStore(
    (state) => state.getListCompanyUserHasAccess
  )
  const roles = useAuthStore((state) => state.roles)
  const hasOrgAdminRole = roles?.some(
    (role) => role.role_name === ERoleType.ORGANIZATION_ADMIN
  )
  const listCompany = getListCompanyUserHasAccess()

  const isOrgAdminOrProjectLead = roles?.some(
    (role) => hasOrgAdminRole || role.role_name === ERoleType.PROJECT_LEAD
  )

  // State to store notification deliverable set ID for notification navigation
  const [notificationDeliverableSetId, setNotificationDeliverableSetId] =
    useState<string>('')

  const isShowProjectLink = useMemo(() => {
    // Show project link unless user has ONLY reviewer/viewer roles
    if (!roles || roles.length === 0) {
      return true
    }

    return !roles.every(
      (role) =>
        role.role_name === ERoleType.REVIEWER ||
        role.role_name === ERoleType.VIEWER
    )
  }, [roles])

  const hasPermission = isUserHasPermissionDeliverableSet(
    listCompany,
    deliverableSet?.company_id || ''
  )

  useEffect(() => {
    if (!hasPermission && deliverableSet && !hasOrgAdminRole) {
      navigate(ROUTES.UNAUTHORIZED, { replace: true })
    }
  }, [hasPermission, hasOrgAdminRole, navigate, deliverableSet])

  // Handle error for notification deliverable set not found
  useEffect(() => {
    if (
      (error as ApiError)?.response?.status === 404 &&
      notificationDeliverableSetId
    ) {
      triggerToast('error', t('notifications.error.notificationNotFound'))
      navigate(ROUTES.DELIVERABLE_SET, { replace: true })
    }
  }, [error, navigate, notificationDeliverableSetId, t])

  // TFL File list store and hooks
  const {
    filters,
    page,
    sort,
    setFilter,
    setPage,
    setSort,
    setDeleteMultipleDocumentIds,
    setDeleteDialogOpen,
    reset: resetDocumentStore,
    onlyOpenComments,
    setOnlyOpenComments,
    showBookmarked,
    setShowBookmarked,
  } = useDocumentStore()

  const { reset: resetTflViewerStore } = useTflViewerStore()

  // Compute filterOnly based on store flags (only one can be active at a time)
  const filterOnly = useMemo(() => {
    if (onlyOpenComments) return EDocumentFilterOnly.OPEN_COMMENTS_ONLY
    if (showBookmarked) return EDocumentFilterOnly.BOOKMARK_ONLY
    return null
  }, [onlyOpenComments, showBookmarked])

  const {
    data: { data = [], pagination = { page: 0, total_pages: 0 } } = {},
    isLoading: isLoadingDocuments,
    isFetching: isFetchingDocuments,
  } = useDocumentList({
    deliverableSetId: id || '',
    search: filters.searchName,
    page,
    sort,
    filterOnly,
  })

  // Reset store and set onlyOpenComments on every navigation
  useEffect(() => {
    resetDocumentStore()
    resetTflViewerStore()

    if (location.state?.onlyOpenComments) {
      setOnlyOpenComments(true)
      globalThis.history.replaceState({}, document.title)
    }

    // Set notification deliverable set ID for notification navigation
    if (location.state?.deliverableSetId) {
      setNotificationDeliverableSetId(location.state.deliverableSetId || '')
      globalThis.history.replaceState({}, location.pathname)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.key])

  const [searchText, setSearchText] = useState<string>('')

  // Debounce search input - only trigger API search after user stops typing
  useEffect(() => {
    const trimmedSearch = searchText?.trim() || ''

    if (trimmedSearch.length >= MIN_SEARCH_LENGTH) {
      const timer = setTimeout(() => {
        setFilter({ searchName: trimmedSearch })
      }, SEARCH_DEBOUNCE_DELAY_MS)

      return () => clearTimeout(timer)
    } else if (filters.searchName !== '') {
      setFilter({ searchName: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText])

  // Reset page when search filter changes
  useEffect(() => {
    setPage(0)
  }, [filters.searchName, setPage])

  // Reset page when filter changes
  useEffect(() => {
    setPage(0)
  }, [filterOnly, setPage])

  // Empty state message for search results
  const noDataMessage = useMemo(() => {
    const isSearching = Boolean(filters.searchName)
    if (isSearching) {
      return <EmptySearchResult translationKey="document" />
    }
    return null
  }, [filters.searchName])

  // Handle view file action - navigate to TFL file detail page
  const handleViewDocumentById = useCallback(
    (document: ResponseDocumentListItem) => {
      if (!id) return

      const documentData = {
        id: document.document_id,
        filename: document.display_name,
      }

      // Update store immediately
      setOpenDocument([documentData])
      setActiveDocumentId(document.document_id)

      // Navigate with state as fallback in case store is reset
      const route = buildDeliverableSetDocument(id)
      navigate(route, {
        state: {
          document: documentData,
          activeDocumentId: document.document_id,
        },
      })
    },
    [id, navigate, setActiveDocumentId, setOpenDocument]
  )

  // Action handler for TFL file actions
  const handleActionClick = useCallback(
    (action: DocumentListAction, item: ResponseDocumentListItem) => {
      switch (action) {
        case 'view':
          handleViewDocumentById(item)
          break
        case 'delete':
          // Delete TFL file functionality to be implemented
          setDeleteMultipleDocumentIds([item.document_id])
          setDeleteDialogOpen(true)

          break
        default:
          break
      }
    },
    [handleViewDocumentById, setDeleteDialogOpen, setDeleteMultipleDocumentIds]
  )

  // Get columns from view-tfl feature
  // Use isFetchingDocuments to force regeneration when query refetches (same pattern as ListPage)
  const columns = useMemo(
    () => getDocumentListColumns(handleActionClick, t, deliverableSet),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleActionClick, t, deliverableSet, isFetchingDocuments]
  )

  // Handle sort change
  const handleSortChange = useCallback(
    (colId: keyof ResponseDocumentListItem | null, dir: SortOrder) => {
      setSort({
        column: colId,
        direction: dir,
      })
    },
    [setSort]
  )

  // Handle View File button click - navigate to TFL files list page
  const handleViewFileButtonClick = useCallback(() => {
    if (!id) return
    const route = buildDeliverableSetDocument(id)
    navigate(route, {
      state: {
        document: { id: '', filename: '' },
        activeDocumentId: null,
      },
    })
  }, [id, navigate])

  const isHaveSyncStatus = useMemo(
    () =>
      !!deliverableSet?.teamwork_sync_status &&
      deliverableSet?.teamwork_sync_status !==
        DeliverableSetSyncStatus.NOT_SYNCED,
    [deliverableSet?.teamwork_sync_status]
  )

  const renderSyncStatusIcon = useCallback(() => {
    switch (deliverableSet?.teamwork_sync_status) {
      case DeliverableSetSyncStatus.SYNCING:
        return (
          <>
            <Loader
              size={12}
              className="size-3 text-foreground-fg-warning-secondary"
            />
            Syncing
          </>
        )
      case DeliverableSetSyncStatus.FAILED:
        return (
          <>
            <CircleAlert className="size-4 text-foreground-fg-error-secondary" />
            Sync failed
          </>
        )
      case DeliverableSetSyncStatus.SYNCED:
        return (
          <>
            <Check className="size-4 text-foreground-fg-success-secondary" />
            Synced
          </>
        )
      case DeliverableSetSyncStatus.DELETED:
        return (
          <>
            <Trash2 className="size-4 text-foreground-fg-error-secondary" />
            Deleted in Teamwork
          </>
        )

      default:
        return null
    }
  }, [deliverableSet?.teamwork_sync_status])

  const renderSyncStatus = useCallback(() => {
    if (isHaveSyncStatus) {
      return (
        <div
          className={cn(
            'flex gap-0.5 items-center px-md py-xxs rounded-sm',
            'text-xs font-medium',
            'border border-solid',
            deliverableSet?.teamwork_sync_status ===
              DeliverableSetSyncStatus.SYNCING
              ? 'border-[#FEDF89] text-[#B54708] bg-[#FFFAEB]'
              : '',
            deliverableSet?.teamwork_sync_status ===
              DeliverableSetSyncStatus.FAILED
              ? 'border-[#FECDCA] bg-[#FEF3F2] text-[#B42318]'
              : '',
            deliverableSet?.teamwork_sync_status ===
              DeliverableSetSyncStatus.SYNCED
              ? 'border-[#ABEFC6] bg-[#ECFDF3] text-[#067647]'
              : '',
            deliverableSet?.teamwork_sync_status ===
              DeliverableSetSyncStatus.DELETED
              ? 'border-[#FECDCA] bg-[#FEF3F2] text-[#B42318]'
              : ''
          )}
        >
          {renderSyncStatusIcon()}
        </div>
      )
    }
  }, [
    deliverableSet?.teamwork_sync_status,
    isHaveSyncStatus,
    renderSyncStatusIcon,
  ])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-50">
        <div className="w-12 h-12 border-b-2 border-blue-600 rounded-full animate-spin" />
      </div>
    )
  }

  if (error || !deliverableSet) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="m-0 mb-1 font-semibold text-display-xs">
            {t('deliverableSet.page.detailTitle')}
          </h1>
          <p className="m-0 text-red-400 text-md">
            {t('deliverableSet.page.notFound')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <Link
            to={ROUTES.DELIVERABLE_SET}
            className="flex-shrink-0 no-underline"
          >
            <span className="font-semibold text-quaternary-500 hover:text-primary-900">
              {t('deliverableSet.page.title')}
            </span>
          </Link>
          <ChevronRight
            size={16}
            className="flex-shrink-0 text-gray-light-300"
          />
          <span className="min-w-0 font-semibold break-all text-primary-900">
            {deliverableSet.deliverable_set_name}
          </span>
        </div>
        <Button
          variant="outlined"
          className="text-sm text-quaternary-600"
          onClick={() => setHistoryOpen(true)}
        >
          {t('deliverableSet.actions.history')}
        </Button>
      </div>

      {/* Page Title */}
      <h1 className="text-display-xs font-semibold !mt-4 break-all">
        {deliverableSet.deliverable_set_name}
        {isHaveSyncStatus ? (
          <div className="inline-flex items-center justify-center gap-2 ml-2">
            {deliverableSet?.teamwork_sync_url ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <a
                    href={deliverableSet?.teamwork_sync_url || ''}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-foreground-fg-quaternary-400"
                    onClick={(event) => {
                      // Prevent triggering parent comment click
                      event.stopPropagation()
                    }}
                    aria-label="Open in Teamwork"
                  >
                    <SquareArrowOutUpRight className="size-4" />
                  </a>
                </TooltipTrigger>
                <TooltipContent>
                  {t('deliverableSet.actions.openInTeamwork')}
                </TooltipContent>
              </Tooltip>
            ) : null}

            <div className="h-4 border-l-0 border-r border-solid border-y-0 border-secondary" />

            {renderSyncStatus()}
          </div>
        ) : null}
      </h1>

      {/* Info Box */}
      <div className="bg-white border border-solid shadow-sm border-secondary rounded-xl">
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="m-0 text-lg font-semibold text-primary-900">
            {t('deliverableSet.detail.title')}
          </h2>
          {isOrgAdminOrProjectLead && (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="text"
                  className="bg-transparent border-none w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
                  onClick={() => setEditingDeliverableSet(deliverableSet)}
                >
                  <FilePenLine size={20} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <span>{t('deliverableSet.actions.edit')}</span>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        <Separator />

        <div className="grid grid-cols-3 gap-4 px-6 py-4 text-sm">
          <div className="flex flex-col">
            <span className="text-secondary-700">
              {t('deliverableSet.detail.name')}
            </span>
            <span className="font-semibold break-words">
              {deliverableSet.deliverable_set_name}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-secondary-700">
              {t('deliverableSet.detail.createBy')}
            </span>
            <span className="font-medium">
              <UserDisplay
                user={deliverableSet.created_by_user}
                showAvatar={false}
              />
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-secondary-700">
              {t('deliverableSet.detail.createDate')}
            </span>
            <span className="font-medium">
              {formatDisplayDate(deliverableSet.created_at, DATE_FORMAT)}
            </span>
          </div>

          <div className="flex flex-col text-sm font-medium">
            <span className="text-secondary-700">
              {t('deliverableSet.detail.company')}
            </span>
            {hasOrgAdminRole ? (
              <Link
                to={`${ROUTES.COMPANY}/${deliverableSet.company_id}`}
                className="font-semibold no-underline break-words text-brand-tertiary-600"
              >
                {deliverableSet.company_name}
              </Link>
            ) : (
              <span className="font-semibold break-words text-primary-900">
                {deliverableSet.company_name}
              </span>
            )}
          </div>
          <div className="flex flex-col">
            <span className="text-secondary-700">
              {t('deliverableSet.detail.project')}
            </span>
            {isShowProjectLink ? (
              <Link
                to={`${ROUTES.PROJECT}/${deliverableSet.project_id}`}
                className="font-semibold no-underline break-words text-brand-tertiary-600"
              >
                {deliverableSet.project_name}
              </Link>
            ) : (
              <span className="font-semibold break-words text-primary-900">
                {deliverableSet.project_name}
              </span>
            )}
          </div>
          <div className="flex flex-col">
            <span className="text-secondary-700">
              {t('deliverableSet.detail.reportingEvent')}
            </span>
            <span className="font-medium">
              {deliverableSet.reporting_event_name}
            </span>
          </div>
        </div>
      </div>

      {/* Approval Information Section */}
      <ApprovalInformationBox
        deliverableSet={deliverableSet}
        isOrgAdminOrProjectLead={isOrgAdminOrProjectLead}
        isHaveFiles={!!data.length}
      />

      {/* File list */}
      <div className="bg-white border border-solid shadow-sm border-secondary rounded-xl">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
          <h2 className="m-0 text-lg font-semibold text-primary-900">
            {t('deliverableSet.fileList.title')}
          </h2>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Input
                placeholder={t('deliverableSet.filters.search')}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full"
                leadingIcon={<Search size={20} className="text-gray-400" />}
                trailingIcon={
                  searchText ? (
                    <X className="w-3.5 h-3.5 cursor-pointer" />
                  ) : undefined
                }
                onTrailingIconClick={
                  searchText
                    ? () => {
                        setSearchText('')
                        setFilter({ searchName: '' })
                      }
                    : undefined
                }
              />
            </div>
            <Popover>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button
                      variant="text"
                      className="relative flex items-center justify-center flex-shrink-0 h-10 p-0 size-8 group"
                      title={t('deliverableSet.filters.filterFiles')}
                    >
                      <Funnel className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
                      {(onlyOpenComments || showBookmarked) && (
                        <div className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-foreground-fg-error-primary" />
                      )}
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  <span>{t('deliverableSet.filters.filterFiles')}</span>
                </TooltipContent>
              </Tooltip>
              <PopoverContent
                className="sm:max-w-[200px] p-0"
                side="bottom"
                align="end"
              >
                <div className="py-1 px-1.5 space-y-0.5">
                  <button
                    type="button"
                    className={cn(
                      'p-sm font-medium text-sm cursor-pointer rounded-sm hover:bg-primary-hover flex gap-2 items-center justify-between w-full text-left bg-transparent border-none',
                      showBookmarked && 'bg-primary-hover'
                    )}
                    onClick={() => {
                      if (!showBookmarked) {
                        setOnlyOpenComments(false)
                      }
                      setShowBookmarked(!showBookmarked)
                    }}
                  >
                    <span>{t('deliverableSet.filters.bookmarkedFiles')}</span>
                    {showBookmarked && (
                      <Check
                        size={16}
                        className="text-foreground-fg-brand-primary-600"
                      />
                    )}
                  </button>
                  <button
                    type="button"
                    className={cn(
                      'p-sm font-medium text-sm cursor-pointer rounded-sm hover:bg-primary-hover flex gap-2 items-center justify-between w-full text-left bg-transparent border-none',
                      onlyOpenComments && 'bg-primary-hover'
                    )}
                    onClick={() => {
                      if (!onlyOpenComments) {
                        setShowBookmarked(false)
                      }
                      setOnlyOpenComments(!onlyOpenComments)
                    }}
                  >
                    <span>{t('deliverableSet.filters.onlyOpenComments')}</span>
                    {onlyOpenComments && (
                      <Check
                        size={16}
                        className="text-foreground-fg-brand-primary-600"
                      />
                    )}
                  </button>
                </div>
              </PopoverContent>
            </Popover>
            <Button
              variant="outlined"
              className="flex items-center gap-1 px-3.5 py-2.5 h-10 text-secondary-700 bg-gray-dark-25"
              onClick={handleViewFileButtonClick}
              disabled={data.length === 0}
            >
              {t('deliverableSet.fileList.actions.viewFile')}
            </Button>
            {isAllowUpload(deliverableSet) && isShowProjectLink && (
              <Button
                color="primary"
                className="flex items-center gap-1 px-3.5 py-2.5 border border-solid border-transparent h-10"
                onClick={() =>
                  navigate(buildFileUploadFromDetailRoute(deliverableSet.id), {
                    state: { from: `/deliverable-set/${deliverableSet.id}` },
                  })
                }
              >
                {t('deliverableSet.fileList.actions.upload')}
              </Button>
            )}
          </div>
        </div>
        <Table
          columns={columns}
          data={data}
          page={(pagination?.page ?? DEFAULT_PAGE) - 1}
          totalPages={pagination?.total_pages ?? 0}
          onPageChange={setPage}
          isLoading={isLoadingDocuments}
          noDataMessage={noDataMessage}
          orderBy={sort.column}
          order={sort.direction}
          onSortChange={handleSortChange}
          onRowClick={(row) => handleViewDocumentById(row)}
        />
      </div>
      <AddDeliverableSetDialog />
      <DeleteDocumentDialog />
      <SendToReviewDialog />
      <CancelApprovalRequestDialog />
      <DelegateDialog />
      <InviteUsersDialog />
      <DeliverableSetHistoryDrawer
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        entityId={id || ''}
      />
    </div>
  )
}

export default DeliverableSetDetailPage
